import {FormBuilderQuestion} from "@/types";
import Label from "@/Components/TailwindUI/Label";
import AnswerableTextField from "@/Components/FormViewer/AnswerableTextField";
import AnswerableToggle from "@/Components/FormViewer/AnswerToggle";
import AnswerableDropdown from "@/Components/FormViewer/AnswerDropdown";
import AnswerMultiSelect from "@/Components/FormViewer/AnswerMultiSelect";
import AnswerableFileUpload from "@/Components/FormViewer/AnswerFileUpload";
import DebugDiv from "@/Components/FormViewer/DebugDiv";
import AnswerableHiddenField from "@/Components/FormViewer/AnswerableHiddenField";
import AnswerableAdvancedDropdown from "@/Components/FormViewer/AnswerableAdvancedDropdown";
import ReactMarkdown from "react-markdown";
import AnswerableScale from "@/Components/FormViewer/AnswerScale";
import AnswerableDate from "@/Components/FormViewer/AnswerableDate";
import AnswerableTime from "@/Components/FormViewer/AnswerableTime";
import AnswerableSignature from "@/Components/FormViewer/AnswerableSignature";
import AnswerableBranchDropdown from "@/Components/FormViewer/AnswerableBranchDropdown";
import rehypeExternalLinks from 'https://esm.sh/rehype-external-links@3'

interface Props {
    question: FormBuilderQuestion,
    offices: { [string: string]: string },
}

export default function Question({
                                     question,
                                     data,
                                     setData,
                                     errors,
                                     deleteFiles,
                                     officeId,
                                     showDev,
                                     settings,
                                     offices
                                 }: Props) {
    if (question.has_hide_logic && shouldHide(question, data)) {
        return <></>
    }
    return (<div
        className={`mb-10 relative ${question.answerable_type == 'App\\Models\\FormBuilder\\AnswerHiddenField' && !showDev ? 'hidden' : ''}`}>
        {showDev ? (<DebugDiv>Question ID <strong>{question.id}</strong> | Answer
            ID <strong>{question.answerable_id}</strong></DebugDiv>) : <></>}
        <Label htmlFor={`${question.id}-target`}
               className="text-gray-700"><ReactMarkdown
            rehypePlugins={[[rehypeExternalLinks, {target: '_blank'}]]}
            className={'[&>p>a]:underline'}>{question.text}</ReactMarkdown>{question.answerable.is_required &&
            <span className="-ml-1">*</span>}</Label>
        {question.hint ?
            <p className="mb-2 px-2 -mt-2 text-sm text-gray-400"
               id={`${question.id}-description`}>{question.hint}</p> : <></>}
        <div className="mt-1">
            <AnswerField question={question} data={data} setData={setData} errors={errors} deleteFiles={deleteFiles}
                         officeId={officeId} settings={settings} offices={offices}/>
            {errors[question.id] !== undefined ? (<p className="mt-2 text-sm text-red-600" id={`${question.id}-error`}>
                {errors[question.id]}
            </p>) : (<></>)}
            {errors[question.id + ".0"] !== undefined ? (
                <p className="mt-2 text-sm text-red-600" id={`${question.id}.0-error`}>
                    {errors[question.id + ".0"]}
                </p>) : (<></>)}
        </div>
    </div>);
}

interface AnswerFieldProps {
    question: FormBuilderQuestion,
}

function AnswerField({question, data, setData, errors, deleteFiles, officeId, settings, offices}: AnswerFieldProps) {
    switch (question.answerable_type) {
        case 'App\\Models\\FormBuilder\\AnswerTextField':
        case 'App\\Models\\FormBuilder\\AnswerSecureTextField':
            return <AnswerableTextField key={question.id} question={question} data={data} setData={setData}
                                        hasError={errors[question.id] !== undefined} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerDate':
            return <AnswerableDate key={question.id} question={question} data={data} setData={setData}
                                   hasError={errors[question.id] !== undefined} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerTime':
            return <AnswerableTime key={question.id} question={question} data={data} setData={setData}
                                   hasError={errors[question.id] !== undefined} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerToggle':
            return <AnswerableToggle key={question.id} question={question} data={data} setData={setData}
                                     hasError={errors[question.id] !== undefined} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerDropdown':
            return <AnswerableDropdown key={question.id} question={question} data={data} setData={setData}
                                       hasError={errors[question.id] !== undefined} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerAdvancedDropdown':
            return <AnswerableAdvancedDropdown key={question.id} question={question} data={data} setData={setData}
                                               hasError={errors[question.id] !== undefined} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerBranchDropdown':
            return <AnswerableBranchDropdown key={question.id} question={question} data={data} setData={setData}
                                             hasError={errors[question.id] !== undefined} settings={settings}
                                             offices={offices} officeId={officeId}/>
        case 'App\\Models\\FormBuilder\\AnswerMultiSelect':
            return <AnswerMultiSelect key={question.id} question={question} data={data} setData={setData}
                                      hasError={errors[question.id] !== undefined} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerFileUpload':
        case 'App\\Models\\FormBuilder\\AnswerSecureFileUpload':
            return <AnswerableFileUpload key={question.id} question={question} data={data} setData={setData}
                                         hasError={errors[question.id] !== undefined} deleteFiles={deleteFiles}
                                         officeId={officeId} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerHiddenField':
            return <AnswerableHiddenField key={question.id} question={question} data={data} setData={setData}
                                          hasError={errors[question.id] !== undefined}
                                          officeId={officeId} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerScale':
            return <AnswerableScale key={question.id} question={question} data={data} setData={setData}
                                    hasError={errors[question.id] !== undefined}
                                    officeId={officeId} settings={settings}/>
        case 'App\\Models\\FormBuilder\\AnswerSignature':
            return <AnswerableSignature key={question.id} question={question} data={data} setData={setData}
                                        hasError={errors[question.id] !== undefined}
                                        officeId={officeId} settings={settings}/>
        default:
            return <>unimplemented</>

    }
}


function shouldHide(question: FormBuilderQuestion, data) {
    const count = question.hide_logics.filter((hideLogic) => {
        // Get the value of the related answer
        // E.g. if an email field relies on hide logic from a name field, we get the name field's question id
        // and get the value from the data[question.id]

        // We also cast null to an empty string if the model is a text field.
        const relatedAnswer = (
            question.answerable_type == 'App\\Models\\FormBuilder\\AnswerTextField'
            && data[hideLogic.value_question_id] === null
        ) ? '' : data[hideLogic.value_question_id];


        //
        // if (relatedAnswer === null || relatedAnswer === undefined) {
        //     return false
        // }

        const relatedAnswerString = relatedAnswer?.toString().trim() || ""
        const relatedAnswerNumber = isNaN(parseFloat(relatedAnswerString)) ? 0 : parseInt("" + parseFloat(relatedAnswerString) * 100);
        const hideLogicNumber = isNaN(parseFloat(hideLogic.value)) ? 0 : parseInt("" + parseFloat(hideLogic.value) * 100);

        // Now we do a check based on the type of rule
        // _I postfix means case-insensitive
        switch (hideLogic.rule_type) {
            case 'EMPTY':
                return !['', null].includes(relatedAnswerString)
            case 'NOT_EMPTY':
                return ['', null].includes(relatedAnswerString)
            case 'CONTAINS':
                return relatedAnswerString.includes(hideLogic.value ?? '');
            case 'CONTAINS_I':
                return relatedAnswerString.toLowerCase().includes(hideLogic.value?.toLowerCase() ?? '');
            case 'EQUALS':
                return relatedAnswerString == (hideLogic.value ?? '');
            case 'EQUALS_I':
                return relatedAnswerString.toLowerCase() == (hideLogic.value?.toLowerCase() ?? '');
            case 'NOT_CONTAINS':
                return !relatedAnswerString.includes(hideLogic.value ?? '');
            case 'NOT_CONTAINS_I':
                return !relatedAnswerString.toLowerCase().includes(hideLogic.value?.toLowerCase() ?? '');
            case 'NOT_EQUALS':
                return relatedAnswerString != (hideLogic.value ?? '');
            case 'NOT_EQUALS_I':
                return relatedAnswerString.toLowerCase() != (hideLogic.value?.toLowerCase() ?? '');
            case 'IN':
                return (relatedAnswer ? relatedAnswer : []).includes(hideLogic.value ?? '');
            case 'NOT_IN':
                return !(relatedAnswer ? relatedAnswer : []).includes(hideLogic.value ?? '');
            case 'FEWER_THAN':
                return relatedAnswerNumber < hideLogicNumber;
            case 'GREATER_THAN':
                return relatedAnswerNumber > hideLogicNumber;
            default:
                return false;
        }
    }).length
    // If the hide logic is for *any* we just need 1 rule to pass, otherwise we need ALL rules
    return count >= (question.hide_logic_type == 'OR' ? 1 : question.hide_logics.length)
}
